import { useAuth } from '@/contexts/auth-context';
import { setServerHealthy as setGlobalServerHealthy } from '@/lib/health-state';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

const API_BASE_URL =
  (typeof window !== 'undefined' && window.__ENV__?.API_BASE_URL) ||
  import.meta.env.VITE_API_URL ||
  'http://localhost:8080';

const HEALTH_CHECK_INTERVAL = 10_000; // 10초
const HEALTH_CHECK_TIMEOUT = 5_000; // 5초

interface HealthCheckContextType {
  isServerHealthy: boolean;
}

const HealthCheckContext = createContext<HealthCheckContextType>({ isServerHealthy: true });

export function useHealthCheck() {
  return useContext(HealthCheckContext);
}

export function HealthCheckProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [isServerHealthy, setIsServerHealthy] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const failCountRef = useRef(0);

  const checkHealth = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT);

      const response = await fetch(`${API_BASE_URL}/actuator/health`, {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        failCountRef.current = 0;
        setIsServerHealthy(true);
        setGlobalServerHealthy(true);
      } else {
        failCountRef.current += 1;
        // 2회 연속 실패 시 서버 다운으로 판단
        if (failCountRef.current >= 2) {
          setIsServerHealthy(false);
          setGlobalServerHealthy(false);
        }
      }
    } catch {
      failCountRef.current += 1;
      if (failCountRef.current >= 2) {
        setIsServerHealthy(false);
        setGlobalServerHealthy(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    // 재인증 시 이전 실패 횟수 초기화
    failCountRef.current = 0;

    // 인증 후 즉시 1회 체크
    checkHealth();

    intervalRef.current = setInterval(checkHealth, HEALTH_CHECK_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, checkHealth]);

  return (
    <HealthCheckContext.Provider value={{ isServerHealthy }}>
      {children}
      {!isServerHealthy && <ServerDownOverlay />}
    </HealthCheckContext.Provider>
  );
}

function ServerDownOverlay() {
  return (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm'>
      <div className='mx-4 w-full max-w-md rounded-lg border bg-background p-8 shadow-xl'>
        <div className='flex flex-col items-center gap-4 text-center'>
          <div className='flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10'>
            <svg
              className='h-8 w-8 text-destructive'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
              strokeWidth={1.5}
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z'
              />
            </svg>
          </div>

          <div>
            <h2 className='text-lg font-semibold'>서버 연결 불가</h2>
            <p className='mt-1 text-sm text-muted-foreground'>
              API 서버에 연결할 수 없습니다.
            </p>
            <p className='text-sm text-muted-foreground'>
              서버가 복구되면 자동으로 연결됩니다.
            </p>
          </div>

          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <svg
              className='h-4 w-4 animate-spin'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              />
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              />
            </svg>
            <span>연결 재시도 중...</span>
          </div>
        </div>
      </div>
    </div>
  );
}
